/**
 * created by hezq on: 2022-10-10
 *
 * description： 工具类
 *
 */
import Cookies from 'js-cookie'
import md5 from 'js-md5'
import { Base64 } from 'js-base64'

/**
 * description: 校验浏览器是否支持localStorage
 *
 * created by hezq on 2022-10-10
 *
 */
function checkLSInBrowser() {
	if (!window.localStorage) {
		throw new Error("您的浏览器版本过低，不支持localStorage")
	} else {
		return true
	}
}

/**
 * description: 校验浏览器是否支持 sessionStorage
 *
 * created by hezq on 2019-11-20
 *
 */
function checkSSInBrowser() {
	if (!window.sessionStorage) {
		throw new Error("您的浏览器版本过低，不支持sessionStorage");
	} else {
		return true;
	}
}

let util = {
	/**
	 * description: 将日期格式化成指定格式的字符串
	 * @param {Date} date 日期
	 * @param {String} fmt 目标字符串格式，支持的字符有：y,M,d,q,w,H,h,m,S，默认：yyyy-MM-dd HH:mm:ss
	 * @return {String} 返回格式化后的日期字符串
	 *
	 * created by hezq on 2019-11-21
	 */
	dateFormat(date, fmt) {
		let type = Object.prototype.toString.call(date)

		// 如果不是Date类型,返回空字符串
		if (type === "[object Date]") {
			// 格式如果不传,默认yyyy-MM-dd HH:mm:ss
			fmt = fmt || 'yyyy-MM-dd HH:mm:ss'

			let obj = {
				'y': date.getFullYear(), // 年份，注意必须用getFullYear
				'M': date.getMonth() + 1, // 月份，注意是从0-11
				'd': date.getDate(), // 日期
				'q': Math.floor((date.getMonth() + 3) / 3), // 季度
				'w': date.getDay(), // 星期，注意是0-6
				'H': date.getHours(), // 24小时制
				'h': date.getHours() % 12 === 0 ? 12 : date.getHours() % 12, // 12小时制
				'm': date.getMinutes(), // 分钟
				's': date.getSeconds(), // 秒
				'S': date.getMilliseconds() // 毫秒
			}

			// 星期枚举
			const week = ['天', '一', '二', '三', '四', '五', '六']
			// 通过指定格式,替代出格式化后的字符串
			for (let i in obj) {
				fmt = fmt.replace(new RegExp(i + '+', 'g'), function(m) {
					let val = String(obj[i])

					if (i === 'w') {
						return (m.length > 2 ? '星期' : '周') + week[val]
					}
					for (let j = 0, len = val.length; j < m.length - len; j++) {
						val = '0' + val
					}
					return m.length === 1 ? val : val.substring(val.length - m.length)
				});
			}

			return fmt
		}

		return date
	},

	/**
	 * description: localStorage 存储
	 * @param {String} key 缓存key
	 * @param {Object} value 缓存值
	 *
	 * created by hezq on 2022-10-10
	 *
	 */
	setLS(key, value) {
		if (checkLSInBrowser()) {
			if (typeof value === 'object') {
				value = JSON.stringify(value)
			}
			window.localStorage.setItem(Base64.encode(key), Base64.encode(value))
		}
	},

	/**
	 * description: 获取单个 localStorage 数据
	 * @param {String} key 缓存key
	 * @param {Boolean} isObject 是否是Object类型
	 *
	 * created by hezq on 2022-10-10
	 *
	 */
	getLS(key, isObject) {
		if (checkLSInBrowser()) {
			let code = window.localStorage.getItem(Base64.encode(key))

			if (code) {
				code = Base64.decode(code)
				if (isObject) {
					try {
						code = JSON.parse(code)
					} catch (e) {
						throw new Error("parse json error")
					}
				}
				return code
			}
		}
		return null
	},

	/**
	 * description: sessionStorage 存储
	 * @param {String} key 缓存key
	 * @param {Object} value 缓存值
	 *
	 * created by hezq on 2019-11-20
	 *
	 */
	setSS(key, value) {
		if (checkSSInBrowser()) {
			if (typeof value === 'object') {
				value = JSON.stringify(value);
			}
			window.sessionStorage.setItem(Base64.encode(key), Base64.encode(value));
		}
	},

	/**
	 * description: 获取单个 sessionStorage 数据
	 * @param {String} key 缓存key
	 * @param {Boolean} isObject 是否是Object类型
	 *
	 * created by hezq on 2019-11-20
	 *
	 */
	getSS(key, isObject) {
		if (checkSSInBrowser()) {
			let code = window.sessionStorage.getItem(Base64.encode(key));

			if (code) {
				code = Base64.decode(code)
				if (isObject) {
					try {
						code = JSON.parse(code);
					} catch (e) {
						throw new Error("parse json error");
					}
				}
				return code;
			}
		}
		return null;
	},

	/**
	 * description: 获取一个随机md5码
	 *
	 * created by hezq on 2022-10-10
	 *
	 */
	GUID() {
		// 获取一段随机数
		function s4() {
			return ((1 + Math.random()) * 0x10000 | 0).toString(16).substring(1)
		}
		// 获取时间戳
		let t = new Date().getTime()

		// 加密返回
		return md5(s4() + s4() + '-' + s4() + '-' + s4() + '-' + s4() + '-' + s4() + s4() + s4() + t)
	},

	/**
	 * description: 获取cookie中的token
	 *
	 * created by hezq on 2021-08-06
	 *
	 */
	getToken() {
		const accessToken = Cookies.get(`${window._behaviourOptions.DOMAIN_URL}.access_token`, {
			domain: window._behaviourOptions.DOMAIN
		})
		const refreshToken = Cookies.get(`${window._behaviourOptions.DOMAIN_URL}.refresh_token`, {
			domain: window._behaviourOptions.DOMAIN
		})
		return {
			accessToken: accessToken,
			refreshToken: refreshToken
		}
	},

	/**
	 * description: 是否是移动端设备
	 *
	 * created by hezq on 2022-11-01
	 *
	 */
	isMobileDevice() {
		const sUserAgent = navigator.userAgent.toLowerCase()

		const bIsIpad = sUserAgent.match(/ipad/i) !== null
		const bIsIphoneOs = sUserAgent.match(/iphone os/i) !== null
		const bIsMidp = sUserAgent.match(/midp/i) !== null
		const bIsUc7 = sUserAgent.match(/rv:1.2.3.4/i) !== null
		const bIsUc = sUserAgent.match(/ucweb/i) !== null
		const bIsAndroid = sUserAgent.match(/android/i) !== null
		const bIsCE = sUserAgent.match(/windows ce/i) !== null
		const bIsWM = sUserAgent.match(/windows mobile/i) !== null
		const bIsWx = sUserAgent.match(/MicroMessenger/i) !== null

		if (bIsIpad || bIsIphoneOs || bIsMidp || bIsUc7 || bIsUc || bIsAndroid || bIsCE || bIsWM || bIsWx) {
			return true
		}

		return false
	},
	
	/**
	 * description: 获取url的origin
	 * @return {String} 
	 *
	 * created by hezq on 2022-11-09 17:21:54
	 *
	 */
	getLocationOrigin() {
		if (window.location.origin) {
			return window.location.origin
		} else {
			return window.location.protocol + '//' + window.location.hostname + (window.location.port ? ':' + window
				.location.port : '')
		}
	}
}

export default util



// WEBPACK FOOTER //
// ./src/util.js